import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "./components/ErrorBoundary";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import ScrollToTop from "./components/ScrollToTop";
import AIChatFloat from "./components/AIChatFloat";
import { Toaster as SonnerToaster } from "./components/ui/sonner";

// Public marketing
import NewHome from "./pages/NewHome";
import Contact from "./pages/Contact";
import ThankYou from "./pages/ThankYou";
import CreateAccount from "./pages/CreateAccount";
import StartFreeTrial from "./pages/StartFreeTrial";
import StartQuiz from "./pages/StartQuiz";

// Relo News / Blog (public, SEO-indexed)
import Insights from "./pages/Insights";
import InsightArticle from "./pages/InsightArticle";
import ReloNews from "./pages/ReloNews";
import ReloNewsArticle from "./pages/ReloNewsArticle";
import Adventures from "./pages/Adventures";
import PortalAdventures from "./pages/portal/PortalAdventures";
import NewsAuthorPage from "./pages/NewsAuthorPage";
import CityGuides from "./pages/CityGuides";
import CityGuide from "./pages/CityGuide";
import PricingPage from "./pages/PricingPage";

// Internal test harness
import UpgradeWallHarness from "./pages/UpgradeWallHarness";

// Legal
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";

// Blueprint AI
import BlueprintIntake from "./pages/BlueprintIntake";
import BlueprintSuccess from "./pages/BlueprintSuccess";
// iter 99 - paid signup → Stripe Checkout
import PaidCheckout from "./pages/PaidCheckout";
// iter 108 - About page
import About from "./pages/About";

// iter 144d - Public partner application
import BecomeAPartner from "./pages/BecomeAPartner";

// iter 136 - Free SEO tools (Readiness Score / Cost Calculator / Visa Finder + hub)
import MoveScore from "./pages/tools/MoveScore";
import CostCalculator from "./pages/tools/CostCalculator";
import VisaFinder from "./pages/tools/VisaFinder";
import ToolsHub from "./pages/tools/ToolsHub";

// Relocation hub + city pages
import RelocationHub from "./pages/RelocationHub";
import RelocationCommunity from "./pages/RelocationCommunity";
import RelocationIntake from "./pages/RelocationIntake";
import CDMXRelocation from "./pages/CDMXRelocation";
import RelocationChecklist from "./pages/relocation/RelocationChecklist";
import RelocationHousing from "./pages/relocation/RelocationHousing";
import RelocationVisas from "./pages/relocation/RelocationVisas";
import RelocationSchools from "./pages/relocation/RelocationSchools";
import RelocationHealthcare from "./pages/relocation/RelocationHealthcare";
import RelocationBanking from "./pages/relocation/RelocationBanking";
import CuratedListings from "./pages/relocation/CuratedListings";
import ListingDetail from "./pages/relocation/ListingDetail";
import MeridaRelocation from "./pages/relocation/MeridaRelocation";
import TulumRelocation from "./pages/relocation/TulumRelocation";
import SanMiguelRelocation from "./pages/relocation/SanMiguelRelocation";
import PuertoVallartaRelocation from "./pages/relocation/PuertoVallartaRelocation";
import PlayaDelCarmenRelocation from "./pages/relocation/PlayaDelCarmenRelocation";

// Member Portal - THE MOVE / THE LIFE
import PortalLogin from "./pages/portal/PortalLogin";
import PortalVerify from "./pages/portal/PortalVerify";
import PortalResetPassword from "./pages/portal/PortalResetPassword";
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalChecklist from "./pages/portal/PortalChecklist";
import PortalDocuments from "./pages/portal/PortalDocuments";
import PortalResources from "./pages/portal/PortalResources";
import PortalSessions from "./pages/portal/PortalSessions";
import PortalSubscription from "./pages/portal/PortalSubscription";
import PortalDiscounts from "./pages/portal/PortalDiscounts";
import PortalLocalDeals from "./pages/portal/PortalLocalDeals";
import PortalHousing from "./pages/portal/PortalHousing";
import PortalCityGuides from "./pages/portal/PortalCityGuides";
import PortalCityGuideDetail from "./pages/portal/PortalCityGuideDetail";
import PortalShortlist from "./pages/portal/PortalShortlist";
import PortalBlueprint from "./pages/portal/PortalBlueprint";
import PortalUpgrade from "./pages/portal/PortalUpgrade";
import PortalRedeem from "./pages/portal/PortalRedeem";
import PortalClassroom from "./pages/portal/PortalClassroom";
import PortalModuleDetail from "./pages/portal/PortalModuleDetail";
import PortalCommunity from "./pages/portal/PortalCommunity";
import PortalEvents from "./pages/portal/PortalEvents";
import PortalProfile from "./pages/portal/PortalProfile";
import PortalProfileEdit from "./pages/portal/PortalProfileEdit";
import PortalMemberProfile from "./pages/portal/PortalMemberProfile";
import PortalPeerCircles from "./pages/portal/PortalPeerCircles";
import PortalPeerCirclesMembers from "./pages/portal/PortalPeerCirclesMembers";
import PortalPeerCirclesInvite from "./pages/portal/PortalPeerCirclesInvite";
import PortalPeerCirclesInbox from "./pages/portal/PortalPeerCirclesInbox";
import PortalPeerCirclesGroups from "./pages/portal/PortalPeerCirclesGroups";
import PortalPeerCirclesGroupDetail from "./pages/portal/PortalPeerCirclesGroupDetail";
import PortalLeaderboard from "./pages/portal/PortalLeaderboard";
import PortalMessages from "./pages/portal/PortalMessages";
import PortalPartnerContact from "./pages/portal/PortalPartnerContact";
import PortalPartnerProfile from "./pages/portal/PortalPartnerProfile";
import PartnerReply from "./pages/PartnerReply";
import PortalScheduleCall from "./pages/portal/PortalScheduleCall";

// Admin
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminListings from "./pages/admin/AdminListings";
import AdminShortlist from "./pages/admin/AdminShortlist";
import AdminCityPhotos from "./pages/admin/AdminCityPhotos";
import AdminLocalDeals from "./pages/admin/AdminLocalDeals";
import FindAProfessional from "./pages/FindAProfessional";
import FindAProfessionalDispatcher from "./pages/FindAProfessionalDispatcher";
import QuizCheckoutRedirect from "./pages/QuizCheckoutRedirect";
import QuizUpsellPage from "./pages/QuizUpsellPage";
import QuizClaimAccount from "./pages/QuizClaimAccount";
import AdminAmbassadors from "./pages/admin/AdminAmbassadors";
import AdminEmailPreview from "./pages/admin/AdminEmailPreview";
import AdminPeerCirclesGroups from "./pages/admin/AdminPeerCirclesGroups";
import AdminAffiliateClicks from "./pages/admin/AdminAffiliateClicks";
import AdminAdventures from "./pages/admin/AdminAdventures";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminPartnerConversations from "./pages/admin/AdminPartnerConversations";
import AdminPartnerApplications from "./pages/admin/AdminPartnerApplications";
import AdminCoaching from "./pages/admin/AdminCoaching";
import AdminClientErrors from "./pages/admin/AdminClientErrors";
import { AdminEventsModeration, AdminReviewsModeration, AdminHousingModeration, AdminLocalDealSubmissions } from "./pages/admin/AdminModeration";

// SEO static-file proxies
import { RobotsRedirect, SitemapRedirect } from "./components/SEORedirect";

// Capture UTM params on first paint (lib/utm.js - non-destructive, runs once)
import { captureUtmFromUrl } from "./lib/utm";
import { capturePromoFromUrl } from "./lib/promo";

function AppRouter() {
  const location = useLocation();
  useEffect(() => { captureUtmFromUrl(); }, []);
  // iter 144n - re-capture promo on every route change so SPA Link
  // navigations like banner → /relocation/blueprint?promo=LAUNCH25
  // still write the code to localStorage. First-touch logic inside
  // capturePromoFromUrl prevents overwrites.
  useEffect(() => { capturePromoFromUrl(); }, [location.pathname, location.search]);
  return (
    <ErrorBoundary scope="app-root">
      <ScrollToTop />
      <Routes>
        {/* SEO static files (served from /public - these proxies catch SPA routing) */}
        <Route path="/robots.txt" element={<RobotsRedirect />} />
        <Route path="/sitemap.xml" element={<SitemapRedirect />} />

        {/* Public - Marketing */}
        <Route path="/" element={<NewHome />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/thank-you" element={<ThankYou />} />

        {/* Public - Free Trial signup (card captured, 15-day trial of Community subscription) */}
        <Route path="/start" element={<StartQuiz />} />
        <Route path="/start-trial" element={<StartFreeTrial />} />
        {/* iter 182.g - Stripe Checkout returns here after card capture */}
        <Route path="/start-trial/complete" element={<StartFreeTrial />} />
        <Route path="/create-account" element={<CreateAccount />} />

        {/* Public - Partner Reply (token-gated, no auth required) */}
        <Route path="/partner-reply/:token" element={<PartnerReply />} />

        {/* Public - Listings */}
        <Route path="/listings" element={<CuratedListings />} />

        {/* Public - Relo News / Blog (SEO indexed, never gated) */}
        <Route path="/relo-news" element={<ReloNews />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/adventures" element={<Adventures />} />
        {/* iter 148 / 149 - Public Find-a-Professional. The dispatcher
            decides between a city landing (e.g. /mexico-city) and a
            partner profile (UUID) based on the slug shape. */}
        <Route path="/find-a-professional" element={<FindAProfessional />} />
        <Route path="/find-a-professional/:slug" element={<FindAProfessionalDispatcher />} />
        {/* iter 152 - Nurture-email deeplink → Stripe checkout. */}
        <Route path="/quiz-checkout" element={<QuizCheckoutRedirect />} />
        {/* iter 153 - Post-payment upsell + password claim. */}
        <Route path="/quiz-upsell" element={<QuizUpsellPage />} />
        <Route path="/quiz-claim" element={<QuizClaimAccount />} />
        <Route path="/relo-news/cities" element={<CityGuides />} />
        <Route path="/relo-news/cities/:citySlug" element={<CityGuide />} />
        <Route path="/relo-news/cities/:citySlug/:neighborhoodSlug" element={<CityGuide />} />
        <Route path="/relo-news/author/:slug" element={<NewsAuthorPage />} />
        <Route path="/relo-news/:slug" element={<ReloNewsArticle />} />
        {/* Legacy aliases - preserve inbound SEO */}
        <Route path="/insights" element={<Insights />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />

        {/* Test harness - internal QA only, not linked anywhere */}
        <Route path="/__upgrade-wall" element={<UpgradeWallHarness />} />

        {/* Public - Blueprint AI funnel */}
        <Route path="/relocation/blueprint" element={<BlueprintIntake />} />
        <Route path="/relocation/blueprint/success" element={<BlueprintSuccess />} />
        {/* iter 99 - retired legacy pricing page; redirect to home pricing strip. */}
        <Route path="/relocation/pricing" element={<Navigate to="/#pricing" replace />} />

        {/* iter 99 - paid signup → Stripe Checkout (no free-trial detour) */}
        <Route path="/checkout/:tier" element={<PaidCheckout />} />

        {/* iter 108 - About / founders */}
        <Route path="/about" element={<About />} />

        {/* iter 144d - Public partner application */}
        <Route path="/become-a-partner" element={<BecomeAPartner />} />

        {/* iter 136 - Free SEO/conversion tools */}
        <Route path="/tools" element={<ToolsHub />} />
        <Route path="/mexico-readiness-score" element={<MoveScore />} />
        <Route path="/mexico-cost-of-living-calculator" element={<CostCalculator />} />
        <Route path="/mexico-visa-finder" element={<VisaFinder />} />

        {/* Public - Relocation hub + city pages */}
        <Route path="/relocation" element={<RelocationHub />} />
        <Route path="/relocation/community" element={<RelocationCommunity />} />
        <Route path="/relocation/intake" element={<RelocationIntake />} />
        <Route path="/relocation/cdmx" element={<CDMXRelocation />} />
        <Route path="/relocation/merida" element={<MeridaRelocation />} />
        <Route path="/relocation/tulum" element={<TulumRelocation />} />
        <Route path="/relocation/san-miguel-de-allende" element={<SanMiguelRelocation />} />
        <Route path="/relocation/puerto-vallarta" element={<PuertoVallartaRelocation />} />
        <Route path="/relocation/playa-del-carmen" element={<PlayaDelCarmenRelocation />} />
        <Route path="/relocation/checklist" element={<RelocationChecklist />} />
        <Route path="/relocation/housing" element={<RelocationHousing />} />
        <Route path="/relocation/visas" element={<RelocationVisas />} />
        <Route path="/relocation/schools" element={<RelocationSchools />} />
        <Route path="/relocation/healthcare" element={<RelocationHealthcare />} />
        <Route path="/relocation/banking" element={<RelocationBanking />} />
        <Route path="/relocation/listings" element={<CuratedListings />} />
        <Route path="/relocation/listings/:slug" element={<ListingDetail />} />

        {/* Legal */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Member Portal - auth */}
        <Route path="/portal" element={<PortalLogin />} />
        <Route path="/portal/verify" element={<PortalVerify />} />
        <Route path="/portal/reset-password" element={<PortalResetPassword />} />

        {/* Member Portal - THE MOVE (trial-gated) */}
        <Route path="/portal/dashboard" element={<PortalDashboard />} />
        <Route path="/portal/blueprint" element={<PortalBlueprint />} />
        <Route path="/portal/checklist" element={<PortalChecklist />} />
        <Route path="/portal/shortlist" element={<PortalShortlist />} />
        <Route path="/portal/documents" element={<PortalDocuments />} />
        <Route path="/portal/resources" element={<PortalResources />} />
        <Route path="/portal/sessions" element={<PortalSessions />} />
        <Route path="/portal/upgrade" element={<PortalUpgrade />} />
        <Route path="/portal/redeem" element={<PortalRedeem />} />
        <Route path="/portal/subscription" element={<PortalSubscription />} />
        <Route path="/portal/schedule-call" element={<PortalScheduleCall />} />

        {/* Member Portal - THE LIFE (paid-only) */}
        <Route path="/portal/community" element={<PortalCommunity />} />
        <Route path="/portal/community/leaderboard" element={<PortalLeaderboard />} />
        {/* Email-link alias: /portal/newcomers → Newcomers Board on PortalCommunity */}
        <Route path="/portal/newcomers" element={<Navigate to="/portal/community?board=newcomers" replace />} />
        <Route path="/portal/events" element={<PortalEvents />} />
        <Route path="/portal/classroom" element={<PortalClassroom />} />
        <Route path="/portal/classroom/:moduleId" element={<PortalModuleDetail />} />
        <Route path="/portal/messages" element={<PortalMessages />} />
        <Route path="/portal/messages/:conversationId" element={<PortalMessages />} />
        <Route path="/portal/discounts" element={<PortalDiscounts />} />
        <Route path="/portal/partners" element={<PortalDiscounts />} />
        <Route path="/portal/partners/:partnerId" element={<PortalPartnerProfile />} />
        <Route path="/portal/partners/:partnerId/contact" element={<PortalPartnerContact />} />
        <Route path="/portal/local-deals" element={<PortalLocalDeals />} />
        <Route path="/portal/housing" element={<PortalHousing />} />
        <Route path="/portal/city-guides" element={<PortalCityGuides />} />
        <Route path="/portal/city-guides/:citySlug" element={<PortalCityGuideDetail />} />

        {/* Member Profile */}
        <Route path="/portal/profile" element={<PortalProfile />} />
        <Route path="/portal/profile/edit" element={<PortalProfileEdit />} />
        <Route path="/portal/members/:userId" element={<PortalMemberProfile />} />

        {/* Peer Circles - iter 73 (Phase 1: hub + members directory + invite) */}
        <Route path="/portal/peer-circles" element={<PortalPeerCircles />} />
        <Route path="/portal/peer-circles/members" element={<PortalPeerCirclesMembers />} />
        <Route path="/portal/peer-circles/invite" element={<PortalPeerCirclesInvite />} />
        {/* Adventures - iter 79 Viator surface, visible to all tiers */}
        <Route path="/portal/adventures" element={<PortalAdventures />} />
        {/* Phase 2 - DMs + requests (iter 75) */}
        <Route path="/portal/peer-circles/inbox" element={<PortalPeerCirclesInbox />} />
        {/* Phase 3 - groups (iter 76) */}
        <Route path="/portal/peer-circles/groups" element={<PortalPeerCirclesGroups />} />
        <Route path="/portal/peer-circles/groups/:groupId" element={<PortalPeerCirclesGroupDetail />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/listings" element={<AdminListings />} />
        <Route path="/admin/shortlist" element={<AdminShortlist />} />
        <Route path="/admin/cities" element={<AdminCityPhotos />} />
        <Route path="/admin/local-deals" element={<AdminLocalDeals />} />
        <Route path="/admin/ambassadors" element={<AdminAmbassadors />} />
        <Route path="/admin/email-preview" element={<AdminEmailPreview />} />
        <Route path="/admin/coaching" element={<AdminCoaching />} />
        <Route path="/admin/events" element={<AdminEventsModeration />} />
        <Route path="/admin/reviews" element={<AdminReviewsModeration />} />
        <Route path="/admin/housing-listings" element={<AdminHousingModeration />} />
        <Route path="/admin/local-deal-submissions" element={<AdminLocalDealSubmissions />} />
        <Route path="/admin/peer-circles/groups" element={<AdminPeerCirclesGroups />} />
        <Route path="/admin/analytics/affiliate-clicks" element={<AdminAffiliateClicks />} />
        <Route path="/admin/adventures" element={<AdminAdventures />} />
        <Route path="/admin/partners" element={<AdminPartners />} />
        <Route path="/admin/partner-conversations" element={<AdminPartnerConversations />} />
        <Route path="/admin/partner-applications" element={<AdminPartnerApplications />} />
        <Route path="/admin/partner-applications/:applicationId" element={<AdminPartnerApplications />} />
        <Route path="/admin/client-errors" element={<AdminClientErrors />} />

        {/* Catch-all → home */}
        <Route path="*" element={<NewHome />} />
      </Routes>
      <FloatingButton />
    </ErrorBoundary>
  );
}

// AI Chat on portal pages, nothing on admin or public marketing.
// WhatsApp float button removed per Step 6 spec - keep marketing surfaces clean.
function FloatingButton() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname.startsWith('/portal')) return <AIChatFloat />;
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <SonnerToaster position="top-center" richColors closeButton />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
